package com.eladfinish.bible.contest.common.usecases

import com.eladfinish.bible.contest.constants.StringConstants.COLON_SPLITTER
import com.eladfinish.bible.contest.constants.StringConstants.MAKAF
import com.eladfinish.bible.contest.extensions.replaceRegex
import com.eladfinish.bible.contest.extensions.splitByRegex
import com.eladfinish.bible.contest.utils.MyStringUtils.replaceSpaceWithNoBreakSpace

class PartitionChapterUseCase {

    operator fun invoke(
        chapter: String,
        isFromSifreyEmet: Boolean, // Tehilim, Mishley, Iyov
    ): List<List<List<String>>> {
        val verses = chapter
            .normalizeWhitespace()
            .trim()
            .replaceRegex(regex = "$MAKAF", replacement = "$MAKAF ")
            .split(COLON_SPLITTER)
            .map { it.trim() }

        val oleVeYored = getOleVeYored()
        val teamimMafsiqim = buildTeamimMafsiqim(
            isFromSifreyEmet = isFromSifreyEmet,
            oleVeYored = oleVeYored,
        )

        return verses
            .mapNotNull { verse ->
                val dividers = findDividers(
                    verse = verse,
                    teamimMafsiqim = teamimMafsiqim,
                    oleVeYored = oleVeYored,
                )
                dividers?.let { partitionVerse(verse = verse, dividers = it) }
            }
    }

    /**
     * Chapter content shown to the user has some spaces replaced with non-breaking spaces
     * (e.g. after the ot-pasuk, to keep the verse number on the same line as its first word —
     * see [com.eladfinish.bible.contest.utils.MyStringUtils.replaceSpaceWithNoBreakSpace]).
     *
     * The word/divider splitting below relies on `\s`, which is Unicode-aware on Android's
     * ICU-backed regex (matches NBSP) but ASCII-only on Kotlin/Native (iOS) and OpenJDK.
     * Without normalization the ot-pasuk would stay glued to the first word on iOS, e.g.
     * the verse-number chip rendering "ג וְהִקְרִיב" instead of "ג".
     *
     * Partition output is rebuilt with regular spaces anyway, so collapsing these display-only
     * whitespace variants to a normal space here is safe and makes every platform behave alike.
     */
    private fun String.normalizeWhitespace(): String =
        replaceRegex(regex = "[\u00A0\u202F\u2009]", replacement = " ")

    private fun buildTeamimMafsiqim(
        isFromSifreyEmet: Boolean,
        oleVeYored: String,
    ): List<String> {
        // Base priority: ole_ve_yored, atnachta, zaqef_qaton
        val firstTier = listOf(oleVeYored, ATNACHTA, ZAQEF_QATON)

        // Second tier: tarcha vs ravia priority depends on sifrey emet
        val secondTier =
            if (isFromSifreyEmet) listOf(RAVIA, TARCHA)
            else listOf(TARCHA, RAVIA)

        return firstTier + secondTier + PASEK
    }

    /**
     * Finds the divider indices for a verse:
     * - Usually: one divider at the first matching taam according to priority order.
     * - Special case: oleVeYored can add a second divider if atnachta is present too.
     *
     * Returns null when no divider was found (matching previous behavior: verse is skipped).
     */
    private fun findDividers(
        verse: String,
        teamimMafsiqim: List<String>,
        oleVeYored: String,
    ): List<Int>? {
        for (taam in teamimMafsiqim) {
            val match = taam.asTaamRegex().find(verse) ?: continue
            val firstDivider = match.range.last

            if (taam == oleVeYored) {
                val atnachtaMatch = ATNACHTA.asTaamRegex().find(verse)
                if (atnachtaMatch != null) {
                    val secondDivider = atnachtaMatch.range.last
                    return listOf(firstDivider, secondDivider)
                }
            }

            return listOf(firstDivider)
        }

        return null
    }

    /**
     * Converts a verse + divider indices into the output structure:
     * [0] = [otPasuk]
     * [1] = reysha words (without otPasuk)
     * [2] = middle words (only if 2 dividers exist)
     * [last] = seyfa words
     */
    @Suppress("KDocUnresolvedReference")
    private fun partitionVerse(
        verse: String,
        dividers: List<Int>,
    ): List<List<String>> {
        val partsNumber = dividers.size + 1 // 1 divider => 2 parts
        val result = MutableList(partsNumber + 1) { mutableListOf<String>() }

        val regexSpace = REGEX_SPACE_EXCEPT_PASEK

        val reysha = verse.take(dividers[0]).trim()
        val otAndReysha = reysha.splitByRegex(regexSpace)

        result[0] = mutableListOf(otAndReysha[0]) // ot pasuk
        result[1] = otAndReysha.subList(fromIndex = 1, toIndex = otAndReysha.size).toMutableList()

        if (partsNumber == 3) {
            val middle = verse.substring(dividers[0], dividers[1])
            result[2] = middle.splitByRegex(regexSpace).toMutableList()
        }

        val seyfa = verse.substring(dividers.last()).trim()
        result[partsNumber] = seyfa.splitByRegex(regexSpace).toMutableList()

        return result
    }

    private companion object {
        private const val ATNACHTA = "֑"
        private const val ZAQEF_QATON = "֔"
        private const val TARCHA = "֖"
        private const val RAVIA = "֗"
        private const val PASEK = "׀"

        // Don't consider pasek as a word in itself
        private const val REGEX_SPACE_EXCEPT_PASEK = "\\s(?!׀)"

        private fun getOleVeYored(): String {
            val ole = "֫"
            val yored = "֥"
            return "$ole.*?\\s?.*?$yored"
        }

        private fun String.asTaamRegex(): Regex =
            "$this.*?\\s".toRegex() // after taam mafsiq must be a space (makaf cannot be)
    }
}
